import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { BatchJobRef, Job, SyntiroHoneyJobStatus } from '../../../types/syntiro_honey';
import type { ReviewFileSeed } from '../../../types/syntiro_honey';
import {
  deleteFile,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import ConfirmationModal from '../../shared/modal/ConfirmationModal';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  describeExtractedRecords,
  findJobErrorDetail,
  formatDuration,
  getJobProgress,
  getJobResultBreakdown,
  getSkippedSheets,
  isTerminalJobStatus,
  jobElapsedMillis,
  summariseJobResult
} from './job_status';

type ChipColor = 'default' | 'info' | 'success' | 'error' | 'warning';

const JOB_STATUS_CHIPS: Record<SyntiroHoneyJobStatus, { label: string; color: ChipColor }> = {
  PENDING: { label: 'Queued', color: 'default' },
  RUNNING: { label: 'Processing', color: 'info' },
  DONE: { label: 'Succeeded', color: 'success' },
  FAILED: { label: 'Failed', color: 'error' },
  CANCELLED: { label: 'Cancelled', color: 'warning' }
};

/**
 * Fallback captions for the two in-flight states, used until the backend's
 * first live-progress report lands (queued jobs, and backends that do not emit
 * stage detail yet). Once a stage is reported, the caption comes from
 * `getJobProgress` instead (per docs/UX_PROCESSING_STATES.md).
 */
const ACTIVE_CAPTION: Record<'PENDING' | 'RUNNING', string> = {
  PENDING: 'Waiting for a worker…',
  RUNNING: 'Extracting with AI — this can take ~30–60s…'
};

/**
 * Re-render every second while `active`, returning the current epoch time in
 * milliseconds. This drives the per-file elapsed timer so a running job visibly
 * ticks instead of looking frozen. When nothing is in flight the interval is
 * torn down, so a settled list does no needless work.
 * @param {boolean} active Whether any job is still running.
 * @returns {number} The current epoch time in milliseconds.
 */
function useTickingNow(active: boolean): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    setNow(Date.now());
    const interval_id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval_id);
  }, [active]);
  return now;
}

interface JobProgressListProps {
  /** One job reference per queued file, as returned by `processBatch`. */
  jobRefs: Array<BatchJobRef>;
  /** Last polled job state per job id (absent until the first poll lands). */
  jobsById: Record<string, Job>;
  /** Original filename per file id, for display alongside each job. */
  filenamesByFileId: Record<string, string>;
  /**
   * File ids deleted from this batch after processing. Owned by the parent (not
   * local state) so it survives a navigate-away-and-back mid-batch, the same way
   * jobRefs/filenamesByFileId already do via PersistedProcessing — otherwise a
   * remount forgets the delete and the row re-renders as if the file still exists.
   */
  deletedFileIds: ReadonlySet<string>;
  /** Called with a file id once its delete succeeds, so the parent can persist it. */
  onFileDeleted: (fileId: string) => void;
  /** Jump to the Review tab pre-filtered to one processed file, when wired. */
  onReviewFile?: (seed: ReviewFileSeed) => void;
}

/**
 * Per-file ingestion progress: one row per job with a status chip, a live
 * elapsed timer and a progress bar while the file is in flight, the failure
 * detail when a job fails, and the extraction breakdown in the two-signal
 * language when it succeeds. The bar is DETERMINATE once the backend reports a
 * pipeline stage (parse → per-sheet extraction → resolve → save), with the
 * caption naming the actual work; before the first report it falls back to
 * honest indeterminate motion (docs/UX_PROCESSING_STATES.md).
 * @param {Array<BatchJobRef>} jobRefs Job references being polled.
 * @param {Record<string, Job>} jobsById Last polled state per job id.
 * @param {Record<string, string>} filenamesByFileId Filename lookup per file id.
 * @returns {JSX.Element}
 */
export default function JobProgressList({
  jobRefs,
  jobsById,
  filenamesByFileId,
  deletedFileIds,
  onFileDeleted,
  onReviewFile
}: JobProgressListProps) {
  const has_active_job = jobRefs.some(
    (ref: BatchJobRef) => !isTerminalJobStatus(jobsById[ref.jobId]?.status)
  );
  const now_ms = useTickingNow(has_active_job);
  // The post-processing escape hatch (Maxine 2026-08-19): delete a just-processed
  // file AND every record it produced, right where the mistake is noticed.
  const [deleteTarget, setDeleteTarget] = useState<ReviewFileSeed | null>(null);
  const [deletingFile, setDeletingFile] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteFile = async (): Promise<void> => {
    if (deleteTarget === null) {
      return;
    }
    setDeletingFile(true);
    setDeleteError(null);
    try {
      await deleteFile(deleteTarget.fileId);
      onFileDeleted(deleteTarget.fileId);
      setDeleteTarget(null);
    } catch (err: unknown) {
      setDeleteError(toProblemDetail(err).detail ?? 'Deleting the file failed.');
    } finally {
      setDeletingFile(false);
    }
  };

  return (
    <>
      <List disablePadding>
        {jobRefs.map((ref: BatchJobRef) => {
          const job: Job | undefined = jobsById[ref.jobId];
          const status: SyntiroHoneyJobStatus = job?.status ?? 'PENDING';
          const status_chip = JOB_STATUS_CHIPS[status];
          const is_active = status === 'PENDING' || status === 'RUNNING';
          const failure_detail = job && status === 'FAILED' ? findJobErrorDetail(job) : null;
          const breakdown = job && status === 'DONE' ? getJobResultBreakdown(job) : null;
          // Fall back to the one-line summary when there is no structured breakdown
          // (e.g. a format the pipeline skipped, which reports a message not counts).
          const result_summary =
            job && status === 'DONE' && breakdown === null ? summariseJobResult(job) : null;
          const skipped_sheets = job && status === 'DONE' ? getSkippedSheets(job) : [];
          // Real pipeline progress, when the backend has reported a stage: the bar
          // becomes determinate and the caption names the actual work ('Reading
          // sheet 3 of 24 — "October"') instead of a generic wait message.
          const live_progress = job && status === 'RUNNING' ? getJobProgress(job) : null;
          const elapsed = job && is_active ? formatDuration(jobElapsedMillis(job, now_ms)) : null;
          const collisions = (breakdown?.duplicates ?? 0) + (breakdown?.conflicts ?? 0);
          const file_deleted = deletedFileIds.has(ref.fileId);
          const filename = filenamesByFileId[ref.fileId] ?? ref.fileId;
          return (
            <ListItem
              key={ref.jobId}
              disableGutters
              sx={{ borderBottom: 1, borderColor: 'divider', display: 'block', py: 1.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BodyText weight="Medium" sx={{ flexGrow: 1 }}>
                  {filename}
                </BodyText>
                {elapsed !== null && (
                  <CaptionText sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {elapsed}
                  </CaptionText>
                )}
                {file_deleted ? (
                  <Chip size="small" label="Deleted" color="default" />
                ) : (
                  <Chip size="small" label={status_chip.label} color={status_chip.color} />
                )}
              </Box>
              {file_deleted && (
                <CaptionText sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                  The file and all its records were deleted. An admin can restore them.
                </CaptionText>
              )}
              {is_active && (
                <Box sx={{ mt: 1 }}>
                  {live_progress !== null ? (
                    <LinearProgress variant="determinate" value={live_progress.percent} />
                  ) : (
                    // No stage reported yet (queued, or a backend without live
                    // progress): honest motion beats a bar frozen at 0%.
                    <LinearProgress variant="indeterminate" />
                  )}
                  <CaptionText sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                    {live_progress?.caption ?? ACTIVE_CAPTION[status]}
                  </CaptionText>
                </Box>
              )}
              {failure_detail !== null && (
                <CaptionText sx={{ mt: 0.5, display: 'block', color: 'error.main' }}>
                  {failure_detail}
                </CaptionText>
              )}
              {breakdown !== null && !file_deleted && (
                <Box sx={{ mt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CaptionText>{describeExtractedRecords(breakdown)}</CaptionText>
                    {/* Per-file jump: land on the Review tab already filtered to THIS
                      file's records, instead of hunting them in the full list. */}
                    {onReviewFile !== undefined &&
                      (breakdown.recordCount > 0 || (breakdown.unchanged ?? 0) > 0) && (
                        <Button
                          size="small"
                          variant="text"
                          sx={{ py: 0, minWidth: 0, textTransform: 'none' }}
                          onClick={() => onReviewFile({ fileId: ref.fileId, filename })}
                        >
                          Review records →
                        </Button>
                      )}
                    {/* The escape hatch: undo a mistaken upload right here, records
                      and all (reversible — an admin can restore the file). */}
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      disabled={deletingFile}
                      sx={{ py: 0, minWidth: 0, textTransform: 'none' }}
                      onClick={() => setDeleteTarget({ fileId: ref.fileId, filename })}
                    >
                      Delete file and records
                    </Button>
                  </Box>
                  {collisions > 0 && (
                    // The scenario-2 hand-off: the pipeline found records that already
                    // exist on Syntiro; the DECISION is made on the review screen.
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip
                        size="small"
                        color="warning"
                        icon={<WarningAmberIcon />}
                        label={`${collisions} record${collisions === 1 ? '' : 's'} already exist on Syntiro`}
                      />
                      {onReviewFile !== undefined && (
                        <Button
                          size="small"
                          variant="text"
                          color="warning"
                          sx={{ py: 0, minWidth: 0, textTransform: 'none' }}
                          onClick={() => onReviewFile({ fileId: ref.fileId, filename })}
                        >
                          Review &amp; resolve →
                        </Button>
                      )}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {breakdown.mapped !== null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="success"
                        label={`${breakdown.mapped} mapped`}
                      />
                    )}
                    {breakdown.needsDisambiguation !== null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        label={`${breakdown.needsDisambiguation} to confirm`}
                      />
                    )}
                    {breakdown.unmapped !== null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="default"
                        label={`${breakdown.unmapped} unmapped`}
                      />
                    )}
                  </Box>
                </Box>
              )}
              {result_summary !== null && !file_deleted && (
                <CaptionText sx={{ mt: 0.5, display: 'block' }}>{result_summary}</CaptionText>
              )}
              {skipped_sheets.length > 0 && !file_deleted && (
                // Coverage honesty: name the sheets that could NOT be read rather than
                // letting a green "Succeeded" chip imply the whole file was ingested.
                // The per-sheet reasons expand on demand — a warning that cannot be
                // inspected is just anxiety.
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{ mt: 1, '&:before': { display: 'none' }, bgcolor: 'transparent' }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon fontSize="small" />}
                    sx={{ p: 0, minHeight: 0 }}
                  >
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      icon={<WarningAmberIcon />}
                      label={`${skipped_sheets.length} sheet${skipped_sheets.length === 1 ? '' : 's'} could not be read`}
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, pl: 1 }}>
                    {skipped_sheets.map((skipped) => (
                      <CaptionText
                        key={skipped.sheet}
                        sx={{ display: 'block', color: 'text.secondary' }}
                      >
                        <strong>{skipped.sheet}</strong> — {skipped.reason}
                      </CaptionText>
                    ))}
                    <CaptionText sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                      Data on these sheets was not extracted — please review them manually or adjust
                      the file and re-upload.
                    </CaptionText>
                  </AccordionDetails>
                </Accordion>
              )}
            </ListItem>
          );
        })}
      </List>
      {deleteError !== null && (
        <CaptionText sx={{ mt: 1, display: 'block', color: 'error.main' }}>
          {deleteError}
        </CaptionText>
      )}
      <ConfirmationModal
        open={deleteTarget !== null}
        title="Delete this file and its records?"
        description={`This deletes "${deleteTarget?.filename ?? ''}" and ALL records extracted from it, including any already approved. An admin can restore it later.`}
        confirm_button_text="Delete file and records"
        confirm_loading={deletingFile}
        handleCloseModal={() => setDeleteTarget(null)}
        handleConfirm={handleDeleteFile}
      />
    </>
  );
}
